// F9 (fase 2, corte 8, ticket #0747) — la entrega programada.
//
// La inmobiliaria (el jefe en sus proyectos, el gerente en toda la org)
// fija o cambia la fecha y la HORA DE INICIO de la entrega material de una
// unidad. Se guarda donde ADR-0144 §1 ya la puso: `assets.attributes
// .fecha_entrega`, un instante ISO con su desfase de Santiago
// («2026-10-28T10:00:00-03:00»). Sin hora de término ni rango (decisión
// de la noche del 2026-10-16: el rango y el recordatorio del día anterior
// son de la fase 3).
//
// En el wire viaja como HORA DE PARED de Santiago: un día «AAAA-MM-DD» y
// una hora «HH:MM» en pasos de 30 minutos, como la visita (F8). Así ni un
// cliente en otra zona ni el cambio de hora chileno corren la entrega: la
// conversión a instante se hace una sola vez, en el servidor.
//
// Sólo para recetas con contraparte EXTERNA (postventa): facility no
// entrega unidades a un propietario.
import { addCalendarDays, CLOSURE_TIME_ZONE, santiagoDate } from "./observation-closure-clock.js";
import { isCalendarDate, isVisitTime } from "./observation-visit.js";
/** Hasta cuántos días corridos hacia adelante se puede fijar la entrega. */
export const UNIT_DELIVERY_MAX_DAYS_AHEAD = 365;
/** El paso de la hora de inicio, en minutos (el mismo de la visita). */
export const UNIT_DELIVERY_TIME_STEP_MINUTES = 30;
/** El tipo de `audit_events` de cada cambio de la entrega. Su id es la
 *  identidad del cambio (clave del aviso y del correo). */
export const UNIT_DELIVERY_SCHEDULED_AUDIT_EVENT = "postventa_unit_delivery_scheduled";
/** ¿Es «HH:MM» una hora de inicio válida? 00:00 a 23:30, en pasos de 30. */
export function isDeliveryTime(time) {
    return isVisitTime(time);
}
/**
 * ¿Se puede fijar la entrega ese día? Desde hoy (en Santiago) hasta
 * `UNIT_DELIVERY_MAX_DAYS_AHEAD` días corridos. Un día pasado nunca: el
 * propietario recibiría el aviso de una entrega que ya no va a ocurrir.
 */
export function deliveryDateSchedulable(date, now) {
    if (!isCalendarDate(date))
        return false;
    const today = santiagoDate(now);
    return date >= today && date <= addCalendarDays(today, UNIT_DELIVERY_MAX_DAYS_AHEAD);
}
const pad = (n, w = 2) => String(n).padStart(w, "0");
const WALL_FORMAT = new Intl.DateTimeFormat("en-US", {
    timeZone: CLOSURE_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
});
function wallParts(utcMs) {
    return Object.fromEntries(WALL_FORMAT.formatToParts(new Date(utcMs))
        .filter((p) => p.type !== "literal")
        .map((p) => [p.type, Number(p.value)]));
}
/** Desfase de America/Santiago en minutos para un instante UTC. */
function santiagoOffsetMinutes(utcMs) {
    const p = wallParts(utcMs);
    const asUtc = Date.UTC(p["year"], p["month"] - 1, p["day"], p["hour"], p["minute"], p["second"]);
    return Math.round((asUtc - utcMs) / 60000);
}
/** Hora de pared en Santiago → ISO con su desfase
 *  («2026-01-15T09:00:00-03:00»). Lo usan el importador de unidades
 *  (ADR-0144) y la ruta de la entrega. */
export function santiagoWallClockToIso(y, mo, d, h, mi) {
    const wall = Date.UTC(y, mo - 1, d, h, mi);
    let off = santiagoOffsetMinutes(wall);
    const off2 = santiagoOffsetMinutes(wall - off * 60000);
    if (off2 !== off)
        off = off2;
    const sign = off < 0 ? "-" : "+";
    const abs = Math.abs(off);
    return `${pad(y, 4)}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
/** El instante que se guarda para un día y una hora de Santiago. */
export function deliveryInstantIso(date, time) {
    const [y, mo, d] = date.split("-").map(Number);
    const [h, mi] = time.split(":").map(Number);
    return santiagoWallClockToIso(y, mo, d, h, mi);
}
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * `fecha_entrega` (sólo el día, o un instante con desfase) → día y hora de
 * Santiago. null si no es legible. Un día sin hora viene de la planilla
 * cuando no trae la hora (ADR-0144): `time` queda null.
 */
export function deliveryWallClock(raw) {
    if (typeof raw !== "string" || raw.length === 0)
        return null;
    if (ISO_DAY_RE.test(raw))
        return isCalendarDate(raw) ? { date: raw, time: null } : null;
    const t = Date.parse(raw);
    if (!Number.isFinite(t))
        return null;
    const p = wallParts(t);
    return {
        date: `${pad(p["year"], 4)}-${pad(p["month"])}-${pad(p["day"])}`,
        time: `${pad(p["hour"])}:${pad(p["minute"])}`,
    };
}
/** ¿Cambió la entrega? Se compara en hora de pared: el mismo día y la
 *  misma hora escritos con otro desfase (o importados como instante) son
 *  la misma entrega, y no se vuelve a avisar. */
export function deliveryChanged(before, after) {
    const a = deliveryWallClock(before);
    const b = deliveryWallClock(after);
    if (a === null || b === null)
        return true;
    return a.date !== b.date || a.time !== b.time;
}
/**
 * ¿La entrega guardada ya ocurrió? Un instante que ya pasó, o un día sin
 * hora (planilla) anterior a hoy en Santiago. Una unidad ya entregada no
 * se vuelve a programar: el propietario, que ya vive en ella, recibiría
 * «Reprogramamos tu entrega» de una entrega que no va a ocurrir.
 */
export function deliveryAlreadyHappened(w, now) {
    if (w === null)
        return false;
    if (w.time === null)
        return w.date < santiagoDate(now);
    return Date.parse(deliveryInstantIso(w.date, w.time)) <= now.getTime();
}
/**
 * ¿El cambio es una REPROGRAMACIÓN (variante «Reprogramamos tu entrega»)?
 * Sólo si había una entrega vigente que se movió. No lo es:
 *   - sin entrega previa (o ilegible);
 *   - una entrega previa que ya pasó;
 *   - el mismo día que traía la planilla sin hora: se confirma la hora,
 *     nada se movió.
 */
export function deliveryIsReschedule(before, after, now) {
    const a = deliveryWallClock(before);
    const b = deliveryWallClock(after);
    if (a === null || b === null)
        return false;
    if (deliveryAlreadyHappened(a, now))
        return false;
    if (a.time === null && a.date === b.date)
        return false;
    return a.date !== b.date || a.time !== b.time;
}
//# sourceMappingURL=unit-delivery.js.map